/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest(".js-product");
      this.productForm = this.section.querySelector(".js-product-form-main");
      this.optionSelectors = this.querySelectorAll(".option-selector");
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener("change", (evt) => {
        if (evt.target.matches('input[type="radio"]')) {
          const optionIndex = parseInt(
            evt.target.closest(".option-selector").dataset.optionIndex,
            10,
          );
          this.findAndSetCompatibleVariant(evt.target.value, optionIndex);
        } else if (evt.target.matches(".custom-select__option")) {
          const optionIndex = parseInt(
            evt.target.closest(".option-selector").dataset.optionIndex,
            10,
          );
          this.findAndSetCompatibleVariant(
            evt.target.dataset.value,
            optionIndex,
          );
        }
        this.handleVariantChange(evt);
      });

      // Add event listener for quantity updates
      this.section.addEventListener("quantity-updated", (evt) => {
        this.updateQMCalculatorWithQuantity(evt.detail.quantity);
      });

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
        this.updateSaleBadges(); // by Nils
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);
      this.updateExan(); // by Nils
      this.updateVolumeDiscounts(); // by Nils
      this.updateUnitPrices(); // by Nils
      this.updateAvailabilityContainer(); //by Nils
      this.updateQMCalculator(); //by Nils
      this.updatePayPalMessage(); //by Nils
      this.updateAvailabilityFlag(); //by Nils
      this.customUpdateWeight(); //by Nils
      this.updateSaleBadges(); //by Nils

      if (!this.preSelection) {
        this.dispatchEvent(
          new CustomEvent("on:variant:change", {
            bubbles: true,
            detail: {
              form: this.productForm,
              variant: this.variant,
              product: this.data.product,
            },
          }),
        );
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll("[data-variant-metafield]").forEach((elem) => {
        elem.classList.toggle(
          "hidden",
          elem.dataset.variantMetafield !== this.variant?.id.toString(),
        );
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector(".js-product-form-main");
      if (!this.productForm) return;

      this.addBtn =
        this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {
        const unavailableStr = this.variant
          ? theme.strings.noStock
          : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists
        ? theme.strings.noStock
        : theme.strings.noVariant;
      el.classList.toggle("is-unavailable", !available);
      el.classList.toggle("is-nonexistent", !exists);

      if (optionEl.classList.contains("custom-select__option")) {
        const em = el.querySelector("em");

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute("title");
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === "false") return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === "selection") {
        // Flag all options as unavailable
        this.querySelectorAll(".js-option").forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (
                option === currVariant.options[optionIndex] &&
                optionIndex !== selectorIndex
              ) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll(".js-option");
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === "dropdown") {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(
                  optionEl,
                  true,
                  variant.available,
                );
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll(
            '.js-option:not([data-value=""])',
          );
          options.forEach((option) => {
            const optionValue =
              selector.dataset.selectorType === "dropdown"
                ? option.dataset.value
                : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (
                  v.options[i] === this.selectedOptions[i] ||
                  this.selectedOptions[i] === null
                ) {
                  matches += 1;
                }
              }
              if (
                v.options[selectorIndex] === optionValue &&
                matches === selectorIndex
              ) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(
              option,
              variantsExist,
              variantsAvailable,
            );
          });
        });
      }
    }

    /**
     * Shows a "-X%" badge on listed option values whose sale percentage differs
     * from their siblings. Each value is evaluated in the context of the other
     * currently selected options, so badges follow the selection on multi-option products.
     * by Nils
     */
    updateSaleBadges() {
      const getPercent = (v) =>
        v.compare_at_price > v.price
          ? Math.round((1 - v.price / v.compare_at_price) * 100)
          : 0;

      this.optionSelectors.forEach((selector, selectorIndex) => {
        if (selector.dataset.selectorType === "dropdown") return;

        const entries = Array.from(selector.querySelectorAll(".js-option")).map(
          (input) => {
            // Variants reachable by picking this value while keeping the other selections
            const matches = this.data.product.variants.filter((v) =>
              v.options.every((val, i) =>
                i === selectorIndex
                  ? val === input.value
                  : this.selectedOptions[i] === null ||
                    val === this.selectedOptions[i],
              ),
            );
            const percent = matches.length
              ? Math.max(...matches.map(getPercent))
              : null;
            return { label: input.nextElementSibling, percent };
          },
        );

        const percents = entries
          .map((e) => e.percent)
          .filter((p) => p !== null);
        const differ = new Set(percents).size > 1;

        entries.forEach(({ label, percent }) => {
          let badge = label.querySelector(".opt-label__badge");
          const show = differ && percent > 0;

          if (!show) {
            if (badge) badge.hidden = true;
            return;
          }

          if (!badge) {
            badge = document.createElement("span");
            badge.className = "opt-label__badge";
            badge.setAttribute("aria-hidden", "true");
            label.appendChild(badge);
          }
          badge.textContent = `-${percent}%`;
          badge.hidden = false;
        });
      });
    }

    /**
     * Updates the QM-Calculator on the PDP when a variant is selected
     * by Nils
     */
    updateQMCalculator() {
      // Get the QM calculator element
      this.qmCalculator = this.section.querySelector(".qm-calculator");
      this.qmValue = this.section.querySelector("#qm-value");
      this.quantityInput = this.section.querySelector(
        '.qty-input__input[name="quantity"]',
      );
      this.selectionWidthHeight = this.section.querySelector("#selection-width-height");

      // If elements don't exist or no variant is selected, return early
      if (!this.qmCalculator || !this.qmValue || !this.variant || !this.selectionWidthHeight) return;

      const variantId = this.variant.id;
      const variantData = this.data.formatted[variantId];

      // Check if measurement info exists for this variant
      if (variantData && variantData.measurement_info) {
        // Show the calculator
        this.qmCalculator.hidden = false;

        // Get the base measurement value and current quantity
        const baseMeasurement = variantData.measurement_info.total_measurement;
        const currentQuantity = this.quantityInput
          ? parseInt(this.quantityInput.value, 10) || 1
          : 1;

        // Change the selection width/height value to the new variant width/height
        this.selectionWidthHeight.textContent = baseMeasurement.toFixed(2).replace(".", ",");

        // Calculate the total measurement based on quantity
        const totalMeasurement = baseMeasurement * currentQuantity;

        // Format the measurement with comma as decimal separator
        const formattedMeasurement = totalMeasurement
          .toFixed(2)
          .replace(".", ",");
        this.qmValue.textContent = formattedMeasurement;

        // Store the base value as a data attribute for future calculations
        this.qmValue.dataset.baseValue = baseMeasurement;

        // Store the current variant ID to ensure we use the correct base measurement
        this.qmValue.dataset.variantId = variantId;
      } else {
        // Hide the calculator if no measurement info is available
        this.qmCalculator.hidden = true;
      }
    }

    /**
     * Updates the QM-Calculator when quantity is changed
     * @param {number} quantity - The new quantity value
     */
    updateQMCalculatorWithQuantity(quantity) {
      // Get the QM calculator element
      this.qmCalculator = this.section.querySelector(".qm-calculator");
      this.qmValue = this.section.querySelector("#qm-value");

      // If elements don't exist or no variant is selected, return early
      if (!this.qmCalculator || !this.qmValue || !this.variant) return;

      const variantId = this.variant.id;
      const variantData = this.data.formatted[variantId];

      // Check if measurement info exists for this variant
      if (variantData && variantData.measurement_info) {
        // Show the calculator
        this.qmCalculator.hidden = false;

        // Get the base measurement value for the current variant
        const baseMeasurement = variantData.measurement_info.total_measurement;

        // Calculate the total measurement based on quantity
        const totalMeasurement = baseMeasurement * quantity;

        // Format the measurement with comma as decimal separator
        const formattedMeasurement = totalMeasurement
          .toFixed(2)
          .replace(".", ",");
        this.qmValue.textContent = formattedMeasurement;
      }
    }

    /**
     * Updates the variants EXAN on variant change.
     * by Nils
     */
    updateExan() {
      // select span where EXAN is displayed
      this.exans = this.section.querySelectorAll(
        ".product-variant-exan--value",
      );
      if (this.exans.length === 0 || !this.variant) return; // return if no spans were found
      const variantId = this.variant.id;
      const variantExan = this.data.formatted[variantId].variant_exan;

      const exanAvailable = this.variant && variantExan;
      this.exans.forEach((exan) => {
        exan.textContent = exanAvailable ? variantExan : "";
      });
    }

    /**
     * Updates the weight on variant change.
     * by Nils
     */
    customUpdateWeight() {
      if (this.weightEl === undefined) {
        // The specs live in the sibling `product-details` section, so this can't
        // be scoped to `this.section` - except inside the quick-add drawer, where
        // the underlying page's specs belong to a different product.
        this.weightEl = this.closest(".quick-add-drawer")
          ? null
          : document.querySelector(
              ".product-details .product-spec__value--weight",
            );
      }
      if (!this.weightEl) return;

      this.weightParentEl =
        this.weightEl.closest(".product-spec__item--weight") || this.weightEl;

      this.weight = this.variant?.weight;

      if (!this.weight) {
        this.weightParentEl.hidden = true;
        return;
      }
      this.weightParentEl.hidden = false;

      const weightUnit = "kg";
      const weightInUnit = this.weight / 1000;

      this.weightEl.innerHTML = `${weightInUnit.toFixed(2).replace(".", ",")} ${weightUnit}`;
    }

    /**
     * Updates the availability flag on variant change.
     * by Nils
     */
    updateAvailabilityFlag() {
      this.availabilityFlag = this.section.querySelector(
        "#product-availability-container",
      );
      if (!this.availabilityFlag || !this.variant) return;
      const iconPill = this.availabilityFlag.querySelector(".icon-pill");
      if (!iconPill) return;

      const variantData = this.data.formatted[this.variant.id] || {};
      const strings = theme.strings.customizations.availability_flag;

      // Mirrors the initial render in snippets/availability-flag.liquid: a variant
      // can override the shop-wide delivery time via the haga.special_delivery_time_*
      // metafields, which are exposed per variant in the product JSON blob.
      const customDeliveryTime =
        Shopify.country === "DE"
          ? variantData.custom_delivery_time_de
          : variantData.custom_delivery_time_rest;

      const defaultShippingDuration =
        Shopify.country === "DE"
          ? strings.shipping_time_germany
          : strings.shipping_time_other;

      let availableShippingDuration;
      if (this.data.product.tags?.includes("NOOR Online")) {
        availableShippingDuration = strings.shipping_time_noor;
      } else if (customDeliveryTime) {
        availableShippingDuration = strings.shipping_text_after_time.replace(
          "[days_value]",
          customDeliveryTime,
        );
      } else {
        availableShippingDuration = defaultShippingDuration;
      }

      const availableText = strings.available;
      const notAvailableText = strings.not_available;

      // Check if variant is available based on inventory policy
      const productAvailable =
        this.variant.available;

      // Remove console.log in production code
      iconPill.classList.toggle("available", productAvailable);
      iconPill.classList.toggle("not-available", !productAvailable);

      // set text
      iconPill.querySelector(".icon-pill__text").innerHTML = productAvailable
        ? availableText
        : notAvailableText;
      const shippingDurationText = this.availabilityFlag.querySelector(
        ".shipping-duration__text",
      );
      if (!shippingDurationText) return;

      if (productAvailable) {
        shippingDurationText.textContent = availableShippingDuration;
      } else {
        shippingDurationText.textContent =
          variantData.variant_restock || strings.shipping_text_not_available;
      }
    }

    /**
     * Updates the PayPal Message price on variant change.
     * by Nils
     */
    updatePayPalMessage() {
      // select div where PayPal Message is displayed
      this.payPalMessage = this.section.querySelector("[data-pp-message]");
      this.quantityInput = this.section.querySelector(
        '.qty-input__input[name="quantity"]',
      );

      // If elements don't exist or no variant is selected, return early
      if (!this.payPalMessage) return;

      const variantId = this.variant.id;
      const variantData = this.data.formatted[variantId];
      const variantPrice = this.variant.price / 100;

      // Check if measurement info exists for this variant

      const currentQuantity = this.quantityInput
        ? parseInt(this.quantityInput.value, 10) || 1
        : 1;

      // Calculate the total measurement based on quantity
      const totalPrice = variantPrice * currentQuantity;

      this.payPalMessage.dataset.ppAmount = totalPrice;
    }

    /**
     * Updates the variants volume discounts on variant change.
     * by Nils
     */
    updateVolumeDiscounts() {
      // select spans where vd-volume/discount/base-price is displayed
      this.table = this.section.querySelector("#custom-volume-discount--table");
      this.volumes = this.section.querySelectorAll(".vd--volume");
      this.volDiscounts = this.section.querySelectorAll(".vd--discount");
      this.volBasePrices = this.section.querySelectorAll(".vd--base-price");
      if (this.volumes.length === 0 || this.volDiscounts.length === 0) return; // return if no spans were found

      const variantPrice = this.variant.price;
      const volumeDiscounts = this.data.volume_discount.volume_discounts; //volume_discounts array of metafield
      const variantMeasurement =
        this.data.formatted[this.variant.id].measurement_info;

      if (volumeDiscounts && variantMeasurement) {
        this.table.hidden = false;
        this.volumes.forEach((vol, i) => {
          vol.textContent = volumeDiscounts[i].volume;
        });

        this.volDiscounts.forEach((discount, i) => {
          const hasDiscount = volumeDiscounts[i].saving;
          discount.textContent = hasDiscount
            ? this.formatPrice(variantPrice * (1 - volumeDiscounts[i].saving))
            : "";
        });

        this.volBasePrices.forEach((volBPrice, i) => {
          const volumeDiscountPrice =
            variantPrice * (1 - volumeDiscounts[i].saving);
          volBPrice.textContent = `${this.formatPrice(
            volumeDiscountPrice / variantMeasurement.total_measurement,
          )}/${
            variantMeasurement.total_measurement_unit === "qm"
              ? "m²"
              : unit === "Stk"
                ? "Stk."
                : variantMeasurement.total_measurement_unit
          }`;
        });
      } else {
        this.table.hidden = true;
      }
    }

    /**
     * Updates the "product-availability container" when a variant is not available
     * by Nils
     */
    updateAvailabilityContainer() {
      this.section.querySelector("#product-availability-container").hidden =
        !this.variant && !this.preSelection;
    }

    /**
     * Updates the variants unit prices on variant change.
     * by Nils
     */
    updateUnitPrices() {
      this.measurements = this.section.querySelectorAll(
        ".custom-total-measurement",
      );
      this.unitPrices = this.section.querySelectorAll(".custom-unit-price");
      this.container = this.section.querySelector(
        ".custom-unit-price__container",
      );
      if (
        this.measurements.length === 0 ||
        this.unitPrices.length === 0 ||
        !this.variant
      )
        return;

      const variantId = this.variant.id;
      const variantMeasurement =
        this.data.formatted[variantId].measurement_info;
      const price = this.variant.price;

      const measurementAvailable = this.variant && variantMeasurement;
      this.measurements.forEach((mes, i) => {
        if (measurementAvailable) {
          this.container.hidden = false;
          const amount = variantMeasurement.total_measurement
            .toString()
            .replace(".", ",");
          const unit = variantMeasurement.total_measurement_unit;
          mes.textContent = `${amount} ${
            unit === "qm" ? "m²" : unit === "Stk" ? "Stk." : unit
          }`;
        } else {
          this.container.hidden = true;
        }
      });

      this.unitPrices.forEach((uPrice, i) => {
        if (measurementAvailable) {
          this.container.hidden = false;
          const amount = variantMeasurement.total_measurement;
          const unit = variantMeasurement.total_measurement_unit;
          const unitPrice = this.formatPrice(price / amount);
          uPrice.textContent = `(${unitPrice}/${
            unit === "qm" ? "m²" : unit === "Stk" ? "Stk." : unit
          })`;
        } else {
          this.container.hidden = true;
        }
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder =
        this.backorder || this.section.querySelector(".backorder");
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === "none") {
          const backorderProdEl = this.backorder.querySelector(
            ".backorder__product",
          );
          const prodTitleEl = this.section.querySelector(".product-title");
          const variantTitle = this.variant.title.includes("Default")
            ? ""
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest(".option-selector");
      if (!selector || selector.dataset.selectorType === "dropdown") return;

      const colorText = selector.querySelector(".js-color-text");
      if (!colorText) return;

      const valueElement =
        evt.target.nextElementSibling?.querySelector(".js-value");
      if (!valueElement) return;

      colorText.textContent = valueElement.textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches("quick-add-drawer")) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery =
          this.mediaGallery || this.section.querySelector("media-gallery");
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`,
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        this.section.querySelector("pickup-availability");
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute("available");
        this.pickUpAvailability.innerHTML = "";
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price =
        this.price ||
        this.section.querySelector(".product-info__price > .price");
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price)
            variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector(".price__current");
        const priceWasEl = this.price.querySelector(".price__was");
        const unitPriceEl = this.price.querySelector(".unit-price");

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML =
            this.data.formatted[this.variant.id].compareAtPrice || "";

        // Custom: update the discount code price.
        const codeAmountEl = this.section.querySelector(
          ".code-discount-block .price__code-amount",
        );
        if (codeAmountEl) {
          const { codeDiscountPrice } = this.data.formatted[this.variant.id];
          if (codeDiscountPrice) codeAmountEl.innerHTML = codeDiscountPrice;
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector(".unit-price__price");
          const unitEl = this.price.querySelector(".unit-price__unit");
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          "price--on-sale",
          this.variant.compare_at_price > this.variant.price,
        );
        this.price.classList.toggle(
          "price--sold-out",
          !this.variant.available && !this.preSelection,
        );
      }

      this.price.querySelector(".price__default").hidden =
        !this.variant && !this.preSelection;
      this.price.querySelector(".price__no-variant").hidden =
        this.variant || this.preSelection;

      // Custom: hide the discount code price when no variant matches.
      const codeDiscountBlock = this.section.querySelector(".code-discount-block");
      if (codeDiscountBlock) {
        codeDiscountBlock.hidden = !this.variant && !this.preSelection;
      }
      const from = this.price.querySelector(".price__from");
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights =
        this.weights || this.section.querySelectorAll(".product-info__weight");
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable
          ? this.data.formatted[this.variant.id].weight
          : "";
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes =
        this.barcodes ||
        this.section.querySelectorAll(".product-info__barcode-value");
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : "";
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector(".product-sku__value");
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : "";
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== "change" || this.dataset.updateUrl === "false")
        return;
      window.history.replaceState(
        {},
        "",
        `${this.dataset.url}?variant=${this.variant.id}`,
      );
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms ||
        this.section.querySelectorAll(
          ".js-product-form-main, .js-instalments-form",
        );

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          const selectedText = selector
            .querySelector(".custom-select__btn")
            .textContent.trim();
          selectedOptions.push(
            selectedText === this.dataset.placeholderText ? null : selectedText,
          );
        } else {
          const selected = selector.querySelector("input:checked");
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find((v) =>
        v.options.every((val, index) => val === selectedOptions[index]),
      );
    }

    formatPrice(price) {
      const formatter = new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      });
      return formatter.format(price / 100);
    }

    /**
     * Finds and sets the first available variant combination when a user selects an option
     * that would otherwise lead to a non-existent variant combination.
     * @param {string} selectedValue - The newly selected option value
     * @param {number} optionIndex - The index of the option being changed (0, 1, or 2)
     */
    findAndSetCompatibleVariant(selectedValue, optionIndex) {
      // Get current selected options
      const currentOptions = [...this.selectedOptions];
      currentOptions[optionIndex] = selectedValue;

      // First, try to find a variant that matches all current options
      let compatibleVariant = this.data.product.variants.find((variant) => {
        return currentOptions.every(
          (option, index) =>
            option === variant.options[index] || option === null,
        );
      });

      // If no exact match found, try to keep as many current options as possible
      if (!compatibleVariant) {
        // Prioritize keeping the newly selected option and options before it
        let bestMatch = null;
        let maxMatches = -1;

        this.data.product.variants.forEach((variant) => {
          // Must match the newly selected option
          if (variant.options[optionIndex] !== selectedValue) return;

          // Try to match as many existing options as possible
          let currentMatches = 0;
          for (let i = 0; i < currentOptions.length; i++) {
            if (i !== optionIndex && currentOptions[i] !== null) {
              if (variant.options[i] === currentOptions[i]) {
                currentMatches++;
              }
            }
          }

          // If this variant is available and offers an equal or better match score,
          // it becomes the new candidate. This prioritizes later variants in case of a tie.
          if (variant.available && currentMatches >= maxMatches) {
            // Update maxMatches if this score is strictly higher, or if bestMatch wasn't viable.
            if (
              currentMatches > maxMatches ||
              !bestMatch ||
              !bestMatch.available
            ) {
              maxMatches = currentMatches;
            }
            // In case of a tie (currentMatches === maxMatches) where both bestMatch and variant are available,
            // this assignment ensures the later processed 'variant' wins.
            // Also handles cases where bestMatch was null or unavailable.
            bestMatch = variant;
          }
        });
        compatibleVariant = bestMatch;
      }

      if (compatibleVariant) {
        // Update all option selectors to match the found variant
        this.optionSelectors.forEach((selector, index) => {
          if (selector.dataset.selectorType === "dropdown") {
            const btn = selector.querySelector(".custom-select__btn");
            btn.textContent = compatibleVariant.options[index];
          } else {
            const input = selector.querySelector(
              `input[value="${compatibleVariant.options[index]}"]`,
            );
            if (input) input.checked = true;
          }
        });

        // Trigger change event to update everything else
        this.dispatchEvent(new Event("change"));
      }
    }
  }

  customElements.define("variant-picker", VariantPicker);
}

